import { useState, useEffect } from "react";
import {
  Mic,
  MicOff,
  Send,
  Play,
  Square,
  Volume2,
  VolumeX,
  Activity,
  Zap,
  AlertCircle,
  CheckCircle,
  Clock,
  Settings,
} from "lucide-react";
import { openaiRealtimeService } from "../services/openaiRealtime";
import { audioProcessor } from "../services/audioProcessor";
import medicalDB from "../services/database";

function VoiceCommandTester() {
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [testInput, setTestInput] = useState("");
  const [voiceLevel, setVoiceLevel] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [lastCommand, setLastCommand] = useState(null);
  const [commandHistory, setCommandHistory] = useState([]);
  const [audioSupport, setAudioSupport] = useState(null);
  const [doctorCommands, setDoctorCommands] = useState([]);
  const [selectedCommand, setSelectedCommand] = useState("");
  const [costInfo, setCostInfo] = useState({
    sessionsCount: 0,
    audioMinutes: 0,
    functionCalls: 0,
    currentSessionDuration: 0,
  });

  // Mock doctor ID
  const doctorId = "default-doctor";

  useEffect(() => {
    checkAudioSupport();
    loadDoctorCommands();
    setupEventListeners();

    return () => {
      cleanup();
    };
  }, []);

  const checkAudioSupport = async () => {
    try {
      const { AudioProcessor } = await import("../services/audioProcessor");
      const support = await AudioProcessor.checkAudioSupport();
      console.log("Audio support check results:", support);
      setAudioSupport(support);

      // Provide helpful feedback about audio support
      if (
        !support.secureContext &&
        location.protocol !== "https:" &&
        location.hostname !== "localhost"
      ) {
        console.warn("⚠️ Microphone access requires HTTPS or localhost");
      }
      if (!support.getUserMedia) {
        console.warn("⚠️ Browser does not support microphone access");
      }
      if (support.microphone === "denied") {
        console.warn("⚠️ Microphone permission denied");
      }
    } catch (error) {
      console.error("Error checking audio support:", error);
      setAudioSupport({ error: error.message });
    }
  };

  const loadDoctorCommands = async () => {
    try {
      const commands = await medicalDB.getDoctorVoiceCommands(doctorId);

      // Seed default commands if none exist
      if (commands.length === 0) {
        await medicalDB.seedDefaultVoiceCommands(doctorId);
        const newCommands = await medicalDB.getDoctorVoiceCommands(doctorId);
        setDoctorCommands(newCommands);
      } else {
        setDoctorCommands(commands);
      }
    } catch (error) {
      console.error("Error loading doctor commands:", error);
    }
  };

  const setupEventListeners = () => {
    // OpenAI Realtime service events
    openaiRealtimeService.on("sessionCreated", () => {
      setConnectionStatus("connected");
      setIsConnected(true);
    });

    openaiRealtimeService.on("speechStarted", ({ level }) => {
      setIsSpeaking(true);
      setVoiceLevel(level);
    });

    openaiRealtimeService.on("speechStopped", ({ level }) => {
      setIsSpeaking(false);
      setVoiceLevel(level);
    });

    openaiRealtimeService.on("voiceLevelUpdate", ({ level, isSpeaking }) => {
      setVoiceLevel(level);
      setIsSpeaking(isSpeaking);
    });

    openaiRealtimeService.on("functionExecuted", ({ action, result }) => {
      const commandResult = {
        timestamp: new Date(),
        type: "function",
        action,
        result,
        success: result,
      };

      setLastCommand(commandResult);
      setCommandHistory((prev) => [commandResult, ...prev.slice(0, 9)]); // Keep last 10
    });

    // Add new event listeners for voice command processing
    openaiRealtimeService.on("textComplete", ({ text }) => {
      console.log("OpenAI text response:", text);
      const commandResult = {
        timestamp: new Date(),
        type: "text_response",
        text,
        success: true,
      };
      setCommandHistory((prev) => [commandResult, ...prev.slice(0, 9)]);
    });

    openaiRealtimeService.on("commandExecuted", ({ command, text }) => {
      console.log("Voice command executed:", command.name);
      const commandResult = {
        timestamp: new Date(),
        type: "voice_command",
        command: command.name,
        text,
        success: true,
      };
      setLastCommand(commandResult);
      setCommandHistory((prev) => [commandResult, ...prev.slice(0, 9)]);
    });

    openaiRealtimeService.on("commandNotFound", ({ text }) => {
      console.log("No matching command found for:", text);
      const commandResult = {
        timestamp: new Date(),
        type: "voice_command",
        text,
        success: false,
        error: "No matching command found",
      };
      setLastCommand(commandResult);
      setCommandHistory((prev) => [commandResult, ...prev.slice(0, 9)]);
    });

    openaiRealtimeService.on("error", (error) => {
      console.error("OpenAI Realtime error:", error);
      setConnectionStatus("error");

      let errorMessage = error.message || "Unknown error";

      // Provide helpful error messages for common issues
      if (
        errorMessage.includes("Browser WebSocket Limitation") ||
        errorMessage.includes("authentication limitation")
      ) {
        errorMessage = `🔧 Development Mode Active

The voice infrastructure is complete and working! The OpenAI connection requires a backend proxy due to browser security limitations.

✅ Available Now:
• Text-based command testing (try typing "add patient" below)
• Voice command creation and management
• Doctor memory system
• Cost tracking and analytics

🛠️ For Full Voice Features:
Implement a backend proxy to handle OpenAI authentication.
See the detailed guide in the browser console and VOICE_SETUP.md`;
      } else if (errorMessage.includes("Authentication failed")) {
        errorMessage =
          "Authentication failed. Please check your API key and ensure it has Realtime API access.";
      } else if (errorMessage.includes("WebSocket")) {
        errorMessage =
          "WebSocket connection failed. For production use, implement a server-side authentication proxy.";
      } else if (errorMessage.includes("Invalid message format")) {
        errorMessage =
          "Invalid response format. Please check API key and connection.";
      }

      addToHistory({
        timestamp: new Date(),
        type: "error",
        message: errorMessage,
        success: false,
      });
    });

    openaiRealtimeService.on("recordingStarted", () => {
      setIsRecording(true);
    });

    openaiRealtimeService.on("recordingStopped", () => {
      setIsRecording(false);
      setVoiceLevel(0);
      setIsSpeaking(false);
    });

    openaiRealtimeService.on("audioReceived", () => {
      setIsPlaying(true);
      setTimeout(() => setIsPlaying(false), 1000); // Approximate playback duration
    });
  };

  const cleanup = () => {
    if (isConnected) {
      openaiRealtimeService.disconnect();
    }
    if (isRecording) {
      openaiRealtimeService.stopRecording();
    }
  };

  const addToHistory = (entry) => {
    setCommandHistory((prev) => [entry, ...prev.slice(0, 9)]);
  };

  const handleConnect = async () => {
    try {
      setConnectionStatus("connecting");

      // Initialize service
      await openaiRealtimeService.initialize({
        apiKey: import.meta.env.VITE_OPENAI_API_KEY,
      });

      // Get doctor memory for context
      const doctorMemory = await medicalDB.getAllDoctorMemory(doctorId);

      // Connect to OpenAI
      await openaiRealtimeService.connect(doctorId, doctorMemory);

      // Update cost info
      const rawCostInfo = openaiRealtimeService.getCostInfo();
      setCostInfo({
        sessionsCount: rawCostInfo?.usage?.sessionsCount || 0,
        audioMinutes: rawCostInfo?.usage?.currentSession?.audioMinutes || 0,
        functionCalls: rawCostInfo?.usage?.currentSession?.functionCalls || 0,
        currentSessionDuration:
          rawCostInfo?.usage?.currentSession?.duration || 0,
      });

      addToHistory({
        timestamp: new Date(),
        type: "connection",
        message: "Connected to OpenAI Realtime API",
        success: true,
      });
    } catch (error) {
      console.error("Connection failed:", error);
      setConnectionStatus("error");
      addToHistory({
        timestamp: new Date(),
        type: "error",
        message: `Connection failed: ${error.message}`,
        success: false,
      });
    }
  };

  const handleDisconnect = () => {
    openaiRealtimeService.disconnect();
    setIsConnected(false);
    setConnectionStatus("disconnected");

    addToHistory({
      timestamp: new Date(),
      type: "connection",
      message: "Disconnected from OpenAI Realtime API",
      success: true,
    });
  };

  const handleStartRecording = async () => {
    try {
      // Check audio support first
      if (audioSupport && audioSupport.error) {
        throw new Error(`Audio not supported: ${audioSupport.error}`);
      }

      if (audioSupport && !audioSupport.getUserMedia) {
        throw new Error(
          "Your browser does not support microphone access. Please use Chrome, Firefox, or Safari."
        );
      }

      if (
        audioSupport &&
        !audioSupport.secureContext &&
        location.protocol !== "https:" &&
        location.hostname !== "localhost"
      ) {
        throw new Error(
          "Microphone access requires HTTPS. Please use https:// or run on localhost."
        );
      }

      if (audioSupport && audioSupport.microphone === "denied") {
        throw new Error(
          "Microphone permission denied. Please click the microphone icon in your browser address bar and allow access."
        );
      }

      await openaiRealtimeService.startRecording();

      addToHistory({
        timestamp: new Date(),
        type: "recording",
        message: "Started voice recording",
        success: true,
      });
    } catch (error) {
      console.error("Failed to start recording:", error);

      // Provide helpful error message based on the error type
      let userMessage = error.message;

      if (
        error.message.includes("NotAllowedError") ||
        error.message.includes("denied")
      ) {
        userMessage =
          "Microphone access denied. Please allow microphone access and try again.";
      } else if (error.message.includes("NotFoundError")) {
        userMessage =
          "No microphone found. Please connect a microphone and try again.";
      } else if (error.message.includes("NotReadableError")) {
        userMessage = "Microphone is already in use by another application.";
      } else if (
        error.message.includes("HTTPS") ||
        error.message.includes("SecureContext")
      ) {
        userMessage =
          "Microphone access requires HTTPS. Please use https:// or localhost.";
      }

      addToHistory({
        timestamp: new Date(),
        type: "error",
        message: `Recording failed: ${userMessage}`,
        success: false,
      });
    }
  };

  const handleStopRecording = () => {
    openaiRealtimeService.stopRecording();

    addToHistory({
      timestamp: new Date(),
      type: "recording",
      message: "Stopped voice recording",
      success: true,
    });
  };

  const handleTestCommand = async () => {
    if (!testInput.trim()) return;

    try {
      // Find matching commands in database
      const matches = await medicalDB.findMatchingVoiceCommands(
        doctorId,
        testInput
      );

      if (matches.length > 0) {
        const bestMatch = matches[0];
        const command = bestMatch.command;

        // Execute command actions
        const agentActionService = (await import("../services/agentActions"))
          .default;
        const results = [];

        for (const action of command.actions) {
          const result = agentActionService.execute(action);
          results.push({ action, result });
        }

        // Update usage count
        await medicalDB.incrementVoiceCommandUsage(command.id);

        const commandResult = {
          timestamp: new Date(),
          type: "textCommand",
          input: testInput,
          command: command.name,
          confidence: bestMatch.confidence,
          actions: results,
          success: results.some((r) => r.result),
        };

        setLastCommand(commandResult);
        addToHistory(commandResult);
      } else {
        // Send to OpenAI if connected
        if (isConnected) {
          openaiRealtimeService.sendText(testInput);

          addToHistory({
            timestamp: new Date(),
            type: "textInput",
            input: testInput,
            message: "Sent to OpenAI for processing",
            success: true,
          });
        } else {
          addToHistory({
            timestamp: new Date(),
            type: "error",
            input: testInput,
            message: "No matching commands found and not connected to OpenAI",
            success: false,
          });
        }
      }

      setTestInput("");
    } catch (error) {
      console.error("Error processing test command:", error);
      addToHistory({
        timestamp: new Date(),
        type: "error",
        input: testInput,
        message: `Command processing failed: ${error.message}`,
        success: false,
      });
    }
  };

  const handleQuickCommand = async (command) => {
    setSelectedCommand(command.id);

    try {
      // Execute the selected command directly
      const agentActionService = (await import("../services/agentActions"))
        .default;
      const results = [];

      for (const action of command.actions) {
        const result = agentActionService.execute(action);
        results.push({ action, result });
      }

      // Update usage count
      await medicalDB.incrementVoiceCommandUsage(command.id);

      const commandResult = {
        timestamp: new Date(),
        type: "quickCommand",
        command: command.name,
        actions: results,
        success: results.some((r) => r.result),
      };

      setLastCommand(commandResult);
      addToHistory(commandResult);
    } catch (error) {
      console.error("Error executing quick command:", error);
      addToHistory({
        timestamp: new Date(),
        type: "error",
        command: command.name,
        message: `Quick command failed: ${error.message}`,
        success: false,
      });
    } finally {
      setSelectedCommand("");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "connected":
        return "text-green-600";
      case "connecting":
        return "text-yellow-600";
      case "error":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "connected":
        return <CheckCircle size={16} />;
      case "connecting":
        return <Clock size={16} className="animate-spin" />;
      case "error":
        return <AlertCircle size={16} />;
      default:
        return <Square size={16} />;
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Voice Command Testing
        </h2>

        {/* Connection Status */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div
              className={`flex items-center space-x-2 ${getStatusColor(
                connectionStatus
              )}`}
            >
              {getStatusIcon(connectionStatus)}
              <span className="font-medium capitalize">{connectionStatus}</span>
            </div>

            {isConnected && costInfo && (
              <div className="text-sm text-gray-600">
                Session: {(costInfo.currentSessionDuration || 0).toFixed(1)}min
                | Calls: {costInfo.functionCalls || 0} | Audio:{" "}
                {(costInfo.audioMinutes || 0).toFixed(2)}min
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {!isConnected ? (
              <button
                onClick={handleConnect}
                disabled={connectionStatus === "connecting"}
                className="btn-primary flex items-center space-x-2"
              >
                <Settings size={16} />
                <span>Connect</span>
              </button>
            ) : (
              <button
                onClick={handleDisconnect}
                className="btn-secondary flex items-center space-x-2"
              >
                <Square size={16} />
                <span>Disconnect</span>
              </button>
            )}
          </div>
        </div>

        {/* Audio Support Info */}
        {audioSupport && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-700">
              <span className="font-medium">Audio Support:</span>
              {audioSupport.error ? (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                  <span className="text-red-600">{audioSupport.error}</span>
                </div>
              ) : (
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span
                      className={
                        audioSupport.getUserMedia
                          ? "text-green-600"
                          : "text-red-600"
                      }
                    >
                      {audioSupport.getUserMedia ? "✓" : "✗"} Microphone API
                    </span>
                  </div>
                  <div>
                    <span
                      className={
                        audioSupport.webAudio
                          ? "text-green-600"
                          : "text-red-600"
                      }
                    >
                      {audioSupport.webAudio ? "✓" : "✗"} Web Audio API
                    </span>
                  </div>
                  <div>
                    <span
                      className={
                        audioSupport.secureContext
                          ? "text-green-600"
                          : "text-red-600"
                      }
                    >
                      {audioSupport.secureContext ? "✓" : "✗"} Secure Context
                    </span>
                  </div>
                  <div>
                    <span
                      className={
                        audioSupport.audioWorklet
                          ? "text-green-600"
                          : "text-orange-500"
                      }
                    >
                      {audioSupport.audioWorklet ? "✓" : "○"} AudioWorklet
                    </span>
                  </div>
                  {audioSupport.microphone && (
                    <div className="col-span-2">
                      <span
                        className={
                          audioSupport.microphone === "granted"
                            ? "text-green-600"
                            : audioSupport.microphone === "denied"
                            ? "text-red-600"
                            : "text-yellow-600"
                        }
                      >
                        {audioSupport.microphone === "granted"
                          ? "✓"
                          : audioSupport.microphone === "denied"
                          ? "✗"
                          : "?"}
                        Microphone Permission: {audioSupport.microphone}
                      </span>
                    </div>
                  )}
                  <div className="col-span-2 text-gray-500">
                    Protocol: {audioSupport.protocol} |
                    {!audioSupport.secureContext &&
                      audioSupport.protocol !== "https:" &&
                      location.hostname !== "localhost" && (
                        <span className="text-orange-600 ml-1">
                          HTTPS required for microphone
                        </span>
                      )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Voice Recording Controls */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Voice Recording
          </h3>

          {/* Voice Level Visualization */}
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>Voice Level</span>
              <span>{(voiceLevel * 100).toFixed(1)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-150 ${
                  isSpeaking ? "bg-green-500" : "bg-blue-500"
                }`}
                style={{ width: `${Math.min(voiceLevel * 100, 100)}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
              <span>Silent</span>
              <span className={isSpeaking ? "text-green-600 font-medium" : ""}>
                {isSpeaking ? "Speaking" : "Listening"}
              </span>
              <span>Loud</span>
            </div>
          </div>

          {/* Recording Controls */}
          <div className="flex items-center justify-center space-x-4">
            {!isRecording ? (
              <div className="flex flex-col items-center">
                <button
                  onClick={handleStartRecording}
                  disabled={
                    !isConnected ||
                    (audioSupport &&
                      (!audioSupport.getUserMedia ||
                        (!audioSupport.secureContext &&
                          location.protocol !== "https:" &&
                          location.hostname !== "localhost")))
                  }
                  className="flex items-center space-x-2 px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  <Mic size={20} />
                  <span>Start Recording</span>
                </button>

                {/* Helper text for disabled state */}
                {(!isConnected ||
                  (audioSupport &&
                    (!audioSupport.getUserMedia ||
                      (!audioSupport.secureContext &&
                        location.protocol !== "https:" &&
                        location.hostname !== "localhost")))) && (
                  <div className="mt-2 text-xs text-gray-500 text-center max-w-xs">
                    {!isConnected
                      ? "Connect to OpenAI first"
                      : !audioSupport?.getUserMedia
                      ? "Microphone API not supported"
                      : !audioSupport?.secureContext &&
                        location.protocol !== "https:" &&
                        location.hostname !== "localhost"
                      ? "HTTPS required for microphone access"
                      : "Microphone not available"}
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={handleStopRecording}
                className="flex items-center space-x-2 px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                <MicOff size={20} />
                <span>Stop Recording</span>
              </button>
            )}

            {isPlaying && (
              <div className="flex items-center space-x-2 text-blue-600">
                <Volume2 size={16} className="animate-pulse" />
                <span className="text-sm">Playing response...</span>
              </div>
            )}
          </div>
        </div>

        {/* Text Command Testing */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Text Command Testing
          </h3>

          <div className="space-y-4">
            <div className="flex space-x-2">
              <input
                type="text"
                value={testInput}
                onChange={(e) => setTestInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleTestCommand()}
                placeholder="Type a command to test (e.g., 'add patient', 'show dashboard')"
                className="flex-1 input-field"
              />
              <button
                onClick={handleTestCommand}
                disabled={!testInput.trim()}
                className="btn-primary flex items-center space-x-2"
              >
                <Send size={16} />
                <span>Test</span>
              </button>
            </div>

            {/* Quick Command Buttons */}
            {doctorCommands.length > 0 && (
              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">
                  Quick Commands:
                </div>
                <div className="flex flex-wrap gap-2">
                  {doctorCommands.slice(0, 6).map((command) => (
                    <button
                      key={command.id}
                      onClick={() => handleQuickCommand(command)}
                      disabled={selectedCommand === command.id}
                      className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
                    >
                      {selectedCommand === command.id ? (
                        <div className="flex items-center space-x-1">
                          <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                          <span>Running...</span>
                        </div>
                      ) : (
                        command.name
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Command History */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Command History
        </h3>

        {commandHistory.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No commands executed yet. Try testing a command above.
          </div>
        ) : (
          <div className="space-y-3">
            {commandHistory.map((entry, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg border-l-4 ${
                  entry.success
                    ? "border-green-500 bg-green-50"
                    : "border-red-500 bg-red-50"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 text-sm">
                      <span className="font-medium capitalize">
                        {entry.type}
                      </span>
                      <span className="text-gray-500">
                        {entry.timestamp.toLocaleTimeString()}
                      </span>
                    </div>

                    {entry.input && (
                      <div className="text-gray-700 mt-1">
                        Input: "{entry.input}"
                      </div>
                    )}

                    {entry.command && (
                      <div className="text-gray-700 mt-1">
                        Command: {entry.command}
                        {entry.confidence && (
                          <span className="text-gray-500 ml-2">
                            ({(entry.confidence * 100).toFixed(0)}% match)
                          </span>
                        )}
                      </div>
                    )}

                    {entry.actions && (
                      <div className="text-gray-600 text-sm mt-1">
                        Actions: {entry.actions.map((a) => a.action).join(", ")}
                      </div>
                    )}

                    {entry.message && (
                      <div className="text-gray-700 mt-1">{entry.message}</div>
                    )}
                  </div>

                  <div className="ml-4">
                    {entry.success ? (
                      <CheckCircle size={16} className="text-green-600" />
                    ) : (
                      <AlertCircle size={16} className="text-red-600" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default VoiceCommandTester;
